import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import supabase from "../../supabase";
import { LoaderSpinner } from "../../components/spinner/loader";
import { Box, Typography } from "@mui/material";
import DataTable from "../../components/ui/DataTable";
import { tokens } from "../../theme";
import RowAction, { RowActions } from "../../components/ui/RowAction";
import { IconEye, IconMoney } from "../../components/ui/icons";
import Swal from "sweetalert2";
import { ticketObjectStatus } from "../../constants/ticketStatus";
import { formatCurrencyManual } from "../../utils/formatters";

const RETRY_DELAY_MS = 2000;

const Bills = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [billFilter, setBillFilter] = useState(null);

  useEffect(() => {
    getTickets();
  }, []);

  const processClientsData = (data) => {
    if (!data || !Array.isArray(data)) return [];
    return data.map((client) => {
      const totalAmount = client.tickets.reduce((acc, ticket) => {
        if (ticket.status !== 2) return acc;

        const ticketTotal =
          ticket.ticketDetails?.reduce((detailAcc, detail) => {
            const subtotal = detail.price * detail.quantity;
            const ivaAmount = subtotal * (detail.ivaPercentage / 100);
            return detailAcc + subtotal + ivaAmount;
          }, 0) || 0;

        return acc + ticketTotal;
      }, 0);

      return {
        ...client,
        facturas: client.tickets.filter((ticket) => ticket.status === 2).length,
        iva: formatCurrencyManual(totalAmount),
      };
    });
  };

  const fetchTickets = async () => {
    const { data, error } = await supabase
      .from("clients")
      .select(
        "*, tickets(*, ticketDetails(*)).eq(status, ticketObjectStatus.autorizada)"
      );
    return { data, error };
  };

  const showErrorWithRetry = (error) => {
    const displayText =
      typeof error === "object"
        ? error?.message || JSON.stringify(error, null, 2)
        : String(error);

    Swal.fire({
      title: "Error obteniendo los datos",
      text: displayText,
      icon: "error",
      confirmButtonText: "Reintentar",
      confirmButtonColor: "#d33",
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.close();
        getTickets();
      }
    });
  };

  const getTickets = async () => {
    setLoading(true);

    const { data, error: errorTickets } = await fetchTickets();

    if (errorTickets) {
      await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
      const { data: retryData, error: retryError } = await fetchTickets();

      if (retryError) {
        setLoading(false);
        showErrorWithRetry(retryError);
      } else {
        setClients(processClientsData(retryData));
        setLoading(false);
      }
    } else {
      setClients(processClientsData(data));
      setLoading(false);
    }
  };

  const columns = [
    {
      // Cliente, N° y CUIT en una celda, igual que en el resto de los listados.
      name: "company",
      label: "Cliente",
      options: {
        filter: false,
        sort: true,
        customBodyRenderLite: (dataIndex) => {
          const item = visibleClients[dataIndex];
          return (
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 }}>
              <Typography sx={{ fontSize: "13.5px", fontWeight: 500 }}>
                {item.company || "Sin razón social"}
              </Typography>
              <Typography
                sx={{ fontSize: "11.5px", color: tokens.ink3, fontVariantNumeric: "tabular-nums" }}
              >
                N° {item.clientId}
                {item.cuit ? ` · ${item.cuit}` : ""}
              </Typography>
            </Box>
          );
        },
      },
    },
    {
      name: "clientId",
      label: "N° Cliente",
      options: { filter: false, sort: true, display: false },
    },
    {
      name: "facturas",
      label: "Facturas",
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({ style: { width: 116, textAlign: "right" } }),
        setCellHeaderProps: () => ({ style: { width: 116, textAlign: "right" } }),
        customBodyRender: (value) => {
          const count = Number(value) || 0;
          if (!count) {
            return (
              <Box component="span" sx={{ fontSize: "13px", color: tokens.ink4, fontVariantNumeric: "tabular-nums" }}>
                0
              </Box>
            );
          }
          // Lo facturable es la acción de esta pantalla: se destaca.
          return (
            <Box
              component="span"
              sx={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                minWidth: 26,
                padding: "2px 8px",
                borderRadius: 999,
                fontSize: "12px",
                fontWeight: 600,
                fontVariantNumeric: "tabular-nums",
                color: "var(--sup-state-info-ink)",
                backgroundColor: "var(--sup-state-info-bg)",
              }}
            >
              {count}
            </Box>
          );
        },
      },
    },
    {
      name: "iva",
      label: "Total con IVA",
      options: {
        filter: false,
        sort: true,
        setCellProps: () => ({ style: { width: 160, textAlign: "right", whiteSpace: "nowrap" } }),
        setCellHeaderProps: () => ({ style: { width: 160, textAlign: "right" } }),
        customBodyRender: (value) => (
          <Box
            component="span"
            sx={{ fontSize: "13.5px", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}
          >
            {value}
          </Box>
        ),
      },
    },
    {
      name: "actions",
      label: "Acciones",
      options: {
        filter: false,
        sort: false,
        setCellProps: () => ({ style: { width: 88, textAlign: "right" } }),
        setCellHeaderProps: () => ({ style: { width: 88, textAlign: "right" } }),
        customBodyRenderLite: (dataIndex) => {
          const item = visibleClients[dataIndex];
          return (
            <RowActions>
              <RowAction
                icon={IconMoney}
                title="Registrar pago"
                onClick={() => navigate("/register-bill-payment", { state: { client: item } })}
              />
              <RowAction
                icon={IconEye}
                title="Ver facturas del cliente"
                onClick={() => navigate("/client-bill-details", { state: { client: item } })}
              />
            </RowActions>
          );
        },
      },
    },
  ];

  const options = {
    responsive: "simple",
    // Los desplegables listaban un valor por cliente: no filtraban nada útil.
    filter: false,
    sortOrder: { name: "facturas", direction: "desc" },
    selectableRows: "none",
    selectableRowsOnClick: true,
    sort: true,
    rowsPerPage: 25,
    downloadOptions: {
      filename: "facturacion-por-cliente.csv",
      separator: ";",
      filterOptions: { useDisplayedColumnsOnly: true, useDisplayedRowsOnly: true },
    },
  };

  const term = search.trim().toLowerCase();

  const visibleClients = clients.filter((client) => {
    const facturas = Number(client.facturas) || 0;
    if (billFilter === "con" && facturas === 0) return false;
    if (billFilter === "sin" && facturas > 0) return false;
    if (!term) return true;
    return [client.company, client.name, client.lastName, client.cuit, client.clientId]
      .filter(Boolean)
      .some((value) => String(value).toLowerCase().includes(term));
  });

  const conFacturas = clients.filter((c) => (Number(c.facturas) || 0) > 0).length;

  const billChips = [
    {
      id: "todos",
      label: `Todos (${clients.length})`,
      active: billFilter === null,
      onClick: () => setBillFilter(null),
    },
    {
      id: "con",
      label: `Con facturas (${conFacturas})`,
      dot: "#6165c4",
      active: billFilter === "con",
      onClick: () => setBillFilter(billFilter === "con" ? null : "con"),
    },
    {
      id: "sin",
      label: `Sin facturas (${clients.length - conFacturas})`,
      dot: "#a09b98",
      active: billFilter === "sin",
      onClick: () => setBillFilter(billFilter === "sin" ? null : "sin"),
    },
  ];

  return loading ? (
    <div
      style={{
        width: "100%",
        height: "200px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <LoaderSpinner />
    </div>
  ) : (
    <Box sx={{ width: "100%" }}>
      <DataTable
        data={visibleClients}
        columns={columns}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: "Buscar por cliente o CUIT…",
        }}
        chips={billChips}
        resultCount={visibleClients.length}
        totalCount={clients.length}
        emptyMessage="No hay clientes con facturas autorizadas"
        options={options}
      />
    </Box>
  );
};

export default Bills;
